// Settings → Connectors and Sync health: the SPA-side DECISIONS, as pure functions with no
// React, Auth0 or DOM imports, so scripts/connectors.selfcheck.ts can pin them (apps/web has no
// vitest; plan §10). Nothing here computes a health figure: the API does (CLAUDE.md, the backend
// is the only calculation authority). This file only builds forms, words things and formats.
import {
  CONNECTOR_SCHEMAS,
  type ConnectorHealth,
  type ConnectorProvider,
  type ConnectorRun,
  type ErrorClass,
  type ImplementedProvider,
  type RunStatus,
} from "@hyphesync/shared";

export const PROVIDER_LABEL: Record<ConnectorProvider, string> = {
  geotab: "Geotab",
  vermeerone: "VermeerOne",
  maintainx: "MaintainX",
  aemp: "AEMP 2.0",
  demo: "Demo (dev only)",
};

// ── Typed form fields per provider (OD-34) ───────────────────────────────────────────────────
export type Field =
  | { key: string; label: string; help?: string; kind: "text"; maxLength: number }
  | { key: string; label: string; help?: string; kind: "number"; min: number; max: number }
  | { key: string; label: string; help?: string; kind: "select"; options: ReadonlyArray<{ value: string; label: string }> }
  | { key: string; label: string; help?: string; kind: "secret" };

/** Keyed by ImplementedProvider: a provider added to CONNECTOR_SCHEMAS fails typecheck here until
 *  it has a form, so it can't reach admins as a blank one. */
export const PROVIDER_FORMS: Record<ImplementedProvider, { config: readonly Field[]; credentials: readonly Field[] }> = {
  demo: {
    config: [
      { key: "account", label: "Account", kind: "text", maxLength: 40, help: "Stands in for a provider account. Changing it re-reads from the start." },
      { key: "units", label: "Units", kind: "number", min: 1, max: 50, help: "Synthetic units per run." },
      { key: "slowMs", label: "Slow mode (ms)", kind: "number", min: 0, max: 600_000, help: "Hold each run this long, to watch overlap prevention. 0 = off." },
      {
        key: "failWith", label: "Fail with", kind: "select", help: "Exercise the failure paths.",
        options: [
          { value: "none", label: "Nothing (succeed)" },
          { value: "auth", label: "Auth failure (pauses after 3)" },
          { value: "upstream", label: "Upstream failure (backs off)" },
        ],
      },
    ],
    credentials: [{ key: "secret", label: "Secret", kind: "secret" }],
  },
};

export const isImplemented = (p: ConnectorProvider): p is ImplementedProvider => p in PROVIDER_FORMS;

export type FormValues = Record<string, string>;

/** A config object as form text (numbers as digits). */
export function toFormValues(fields: readonly Field[], config: Record<string, unknown>): FormValues {
  return Object.fromEntries(fields.map((f) => [f.key, config[f.key] === undefined || config[f.key] === null ? "" : String(config[f.key])]));
}

/** The provider schema's defaults, as form text: what a new connector's form starts with. */
export function defaultFormValues(provider: ImplementedProvider): FormValues {
  const parsed = CONNECTOR_SCHEMAS[provider].config.safeParse({});
  return toFormValues(PROVIDER_FORMS[provider].config, parsed.success ? (parsed.data as Record<string, unknown>) : {});
}

/** Form text → a value object, validated with the provider's own schema (the API validates it
 *  again). On CREATE an empty field is left out, so the schema's default applies. On EDIT an
 *  empty field is refused: falling back to the default there would silently change a stored
 *  value (for an account field, re-pointing the connector). */
export function parseForm(
  provider: ImplementedProvider,
  which: "config" | "credentials",
  values: FormValues,
  mode: "create" | "edit" = "create",
): { ok: true; value: Record<string, unknown> } | { ok: false; message: string } {
  const fields = PROVIDER_FORMS[provider][which];
  const raw: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.key] ?? "";
    if (mode === "edit" && f.kind !== "secret" && v.trim() === "") return { ok: false, message: `${f.label}: enter a value.` };
    if (f.kind === "number") {
      if (v.trim() === "") continue;
      if (!/^-?\d+$/.test(v.trim())) return { ok: false, message: `${f.label}: enter a whole number.` };
      raw[f.key] = Number(v.trim());
    } else if (f.kind === "secret") {
      raw[f.key] = v; // never trimmed: a secret is exactly what was typed
    } else if (v.trim() !== "") {
      raw[f.key] = v.trim();
    }
  }
  const parsed = CONNECTOR_SCHEMAS[provider][which].safeParse(raw);
  if (parsed.success) return { ok: true, value: parsed.data as Record<string, unknown> };
  const issue = parsed.error.issues[0];
  const field = fields.find((f) => f.key === String(issue?.path[0] ?? ""));
  const bound = field?.kind === "number" ? ` (${field.min}–${field.max})` : "";
  return { ok: false, message: field ? `${field.label}: check this value${bound}.` : "Check the form and try again." };
}

/** Config fields that re-point the connector (their change resets the sync cursor, so the next
 *  run re-reads from the start): the provider's own `accountKeys`, changed by this edit. */
export function changedAccountFields(provider: ImplementedProvider, before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  const keys: readonly string[] = CONNECTOR_SCHEMAS[provider].accountKeys;
  return keys
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => PROVIDER_FORMS[provider].config.find((f) => f.key === k)?.label ?? k);
}

/** Only the fields an edit actually changed: PATCH writes what is sent, so sending unchanged
 *  values would make a concurrent edit to them look like ours. */
export function connectorPatch(
  before: { name: string; config: Record<string, unknown>; cadenceSecs: number },
  after: { name: string; config: Record<string, unknown>; cadenceSecs: number },
): { name?: string; config?: Record<string, unknown>; cadenceSecs?: number } {
  const patch: { name?: string; config?: Record<string, unknown>; cadenceSecs?: number } = {};
  if (after.name.trim() !== before.name) patch.name = after.name.trim();
  if (JSON.stringify(after.config) !== JSON.stringify(before.config)) patch.config = after.config;
  if (after.cadenceSecs !== before.cadenceSecs) patch.cadenceSecs = after.cadenceSecs;
  return patch;
}

// ── Cadence presets (OD-34) ───────────────────────────────────────────────────────────────────
export const CADENCE_PRESETS = [300, 600, 900, 1800, 3600, 21600, 43200, 86400] as const;

export function cadenceLabel(secs: number): string {
  if (secs % 3600 === 0) return secs === 3600 ? "Every hour" : `Every ${secs / 3600} hours`;
  if (secs % 60 === 0) return `Every ${secs / 60} min`;
  return `Every ${secs} s`;
}

/** The presets, plus the connector's current cadence if it isn't one (a multiple of 5 min the
 *  API accepts but no preset offers, e.g. 20 min), so the select never silently shows another
 *  value than the stored one. */
export function cadenceOptions(current?: number): Array<{ value: number; label: string }> {
  const values: number[] = [...CADENCE_PRESETS];
  if (current !== undefined && !values.includes(current)) values.push(current);
  return values.sort((a, b) => a - b).map((v) => ({ value: v, label: cadenceLabel(v) }));
}

// ── Wording ───────────────────────────────────────────────────────────────────────────────────
export const HEALTH_TEXT: Record<ConnectorHealth, { label: string; tone: "ok" | "bad" | "muted" }> = {
  healthy: { label: "Healthy", tone: "ok" },
  failing: { label: "Failing", tone: "bad" },
  paused: { label: "Paused: needs attention", tone: "bad" },
  late: { label: "Late: not run on schedule", tone: "bad" },
  awaiting_run: { label: "Waiting for the next run", tone: "muted" },
  never_run: { label: "Not run yet", tone: "muted" },
  off: { label: "Off", tone: "muted" },
};

export const RUN_STATUS_TEXT: Record<RunStatus, { label: string; tone: "ok" | "bad" | "muted" }> = {
  ok: { label: "OK", tone: "ok" },
  error: { label: "Error", tone: "bad" },
  skipped: { label: "Skipped", tone: "muted" },
  queued: { label: "Queued", tone: "muted" },
  running: { label: "Running", tone: "muted" },
};

export const ERROR_CLASS_TEXT: Record<ErrorClass, string> = {
  auth: "Credentials refused",
  rate_limit: "Rate limited",
  upstream: "Provider unavailable",
  schema: "Unexpected response",
  internal: "HypheSync error",
};

export const toneClass = (tone: "ok" | "bad" | "muted") => (tone === "ok" ? "text-ok" : tone === "bad" ? "text-bad" : "text-muted");

/** "3 min ago" / "in 2 min", measured against the API's generatedAt (not the browser clock). */
export function relativeTime(iso: string | null, nowIso: string): string {
  if (!iso) return "Never";
  const diff = Date.parse(iso) - Date.parse(nowIso);
  const abs = Math.abs(diff);
  const word =
    abs < 45_000 ? null
      : abs < 3_600_000 ? `${Math.round(abs / 60_000)} min`
        : abs < 86_400_000 ? `${Math.round(abs / 3_600_000)} h`
          : `${Math.round(abs / 86_400_000)} d`;
  if (word === null) return diff > 0 ? "in under a minute" : "just now";
  return diff > 0 ? `in ${word}` : `${word} ago`;
}

/** Rounded ONCE, then split, so a carry never prints "60.0 s" or "1 min 60 s". */
/** "Next run" as shown on Sync health: a slot already due reads as due (or waiting for the run
 *  in progress), never as "3 min ago". */
export function nextRunText(c: { enabled: boolean; status: string; running: boolean; nextRunAt: string | null }, nowIso: string): string {
  if (!c.enabled || c.status !== "active" || c.nextRunAt === null) return "Not scheduled";
  if (Date.parse(c.nextRunAt) <= Date.parse(nowIso)) return c.running ? "After the current run" : "Due now";
  return relativeTime(c.nextRunAt, nowIso);
}

export function durationText(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms} ms`;
  const tenths = Math.round(ms / 100);
  if (tenths < 600) return `${(tenths / 10).toFixed(1)} s`;
  const secs = Math.round(ms / 1000);
  return `${Math.floor(secs / 60)} min ${secs % 60} s`;
}

export const countsText = (counts: Record<string, number>): string =>
  Object.entries(counts).map(([k, v]) => `${k} ${v}`).join(" · ") || "—";

export function bytesText(bytes: number | null): string {
  if (bytes === null) return "unknown";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

// ── Polling (OD-15, OD-34) ────────────────────────────────────────────────────────────────────
export const POLL_MS = 60_000;
export const FAST_POLL_MS = 15_000;
/** After Run now, poll fast this long so the run's result shows promptly. */
export const FAST_POLL_FOR_MS = 120_000;

/** How long until the next refresh: none while the tab is hidden (a visible-again refresh
 *  happens at once instead), fast for a while after Run now, else every minute. */
export function pollDelay(visible: boolean, nowMs: number, fastUntilMs: number): number | null {
  if (!visible) return null;
  return nowMs < fastUntilMs ? FAST_POLL_MS : POLL_MS;
}

// ── Confirmations (OD-34) ─────────────────────────────────────────────────────────────────────
/** The one wording of each confirm, shared by Settings → Connectors and Sync health. */
export const CONFIRM = {
  turnOff: (name: string) => `Turn off ${name}? It stops syncing until it is turned back on.`,
  resume: (name: string) => `Resume ${name}? It runs again on the next tick, with the credentials it has now.`,
  runNow: (name: string) => `Run ${name} now? It is queued and runs within a minute, outside its schedule.`,
  replaceCredentials: (name: string) =>
    `Replace the stored credentials for ${name}? The old ones are discarded and the next run re-reads everything from the start.`,
  accountChange: (name: string, fields: string[]) =>
    `Changing ${fields.join(", ")} points ${name} at another account: the next run re-reads everything from the start. Save?`,
};

// ── Run history merging ───────────────────────────────────────────────────────────────────────
/** Newest first, as the API orders them: queued_at DESC, id DESC. */
const newerFirst = (a: ConnectorRun, b: ConnectorRun) =>
  a.queuedAt === b.queuedAt ? (a.id < b.id ? 1 : a.id > b.id ? -1 : 0) : a.queuedAt < b.queuedAt ? 1 : -1;

/** Every run the page has been sent for a connector (polls + "Load older" pages), each once in
 *  its latest state. Merging, rather than replacing, keeps older pages contiguous with the
 *  inline page after a poll moves it forward. */
export function mergeRuns(known: readonly ConnectorRun[], incoming: readonly ConnectorRun[]): ConnectorRun[] {
  const byId = new Map(known.map((r) => [r.id, r]));
  for (const r of incoming) byId.set(r.id, r);
  return [...byId.values()].sort(newerFirst);
}

/** The most runs a card keeps in memory. Past it, the card drops back to the newest page. */
export const MAX_HISTORY_RUNS = 200;

/** Per connector on Sync health: every run the card shows, and where "Load older" continues. */
export interface RunHistory { runs: ConnectorRun[]; olderCursor: string | null; pagedOlder: boolean }

/** A poll's newest page applied to what the card holds. Without loaded older pages the card is
 *  just that page. With them, the new page is merged only if it CONNECTS to what is held (it
 *  contains the newest run held, or is every run there is); otherwise more runs landed between
 *  polls than one page holds, and keeping the older pages would leave a hole no button fills, so
 *  the card drops back to the new page. Likewise past MAX_HISTORY_RUNS, so a tab left open all
 *  day stays bounded. */
export function applyPoll(prev: RunHistory | undefined, page: readonly ConnectorRun[], cursor: string | null): RunHistory {
  const fresh: RunHistory = { runs: [...page], olderCursor: cursor, pagedOlder: false };
  if (!prev?.pagedOlder) return fresh;
  const newestHeld = prev.runs[0];
  const connects = newestHeld === undefined || cursor === null || page.some((r) => r.id === newestHeld.id);
  if (!connects) return fresh;
  const runs = mergeRuns(prev.runs, page);
  if (runs.length > MAX_HISTORY_RUNS) return fresh;
  return { runs, olderCursor: prev.olderCursor, pagedOlder: true };
}

/** A "Load older" page appended to what the card holds, or null when the card no longer
 *  continues from the cursor it was requested with (a poll moved the newest page, or reset the
 *  card, while the request was in flight). Appending it then would leave a gap, so the caller
 *  asks again from the card's current cursor instead (never a silent no-op). */
export function applyOlderPage(prev: RunHistory, requestedCursor: string, page: readonly ConnectorRun[], cursor: string | null): RunHistory | null {
  if (prev.olderCursor !== requestedCursor) return null;
  return { runs: mergeRuns(prev.runs, page), olderCursor: cursor, pagedOlder: true };
}
/** How many times "Load older" re-asks after the card moved underneath it. */
export const LOAD_OLDER_ATTEMPTS = 3;

// ── Card decisions (OD-34) ────────────────────────────────────────────────────────────────────
export type Panel = "edit" | "credentials" | null;

/** What a Settings → Connectors card locks while a panel is open. Either panel holds typed input
 *  that switching panels would throw away, and the actions that change the connector's version
 *  (on/off, Resume) would make Edit's save stale, so it would be refused and the form restarted. Test and Run now change nothing. */
export function cardLocks(panel: Panel): { toggle: boolean; resume: boolean; editButton: boolean; credentialsButton: boolean } {
  return {
    toggle: panel === "edit",
    resume: panel === "edit",
    editButton: panel === "credentials",
    credentialsButton: panel === "edit",
  };
}

/** What Sync health does after an admin action: a queued or resumed connector is polled fast so
 *  its result shows promptly; a refusal only means the card may be stale, so refresh once. */
export function afterAction(outcome: "done" | "cancelled" | "refused"): "fast_poll" | "refresh_once" | "nothing" {
  return outcome === "done" ? "fast_poll" : outcome === "refused" ? "refresh_once" : "nothing";
}

/** Does a refused edit-form save restart the form from the current values? ONLY when the refusal
 *  says the connector changed underneath it (its baseline and version are stale, and re-sending
 *  them would revert that change). Every other refusal (a name clash, a bad value, a network
 *  error) keeps what the admin typed, so they can correct it and save again. */
export const restartsEditForm = (errorCode: string | null): boolean => errorCode === "connector_changed";

/** Refusals about the connector's STATE: they apply for as long as that state holds. Every other
 *  refusal (already running, test cooldown, someone else's change, rate limit, network) is about
 *  a MOMENT and is gone at the next refresh. */
const STATE_REFUSALS = new Set(["connector_inactive", "credentials_missing"]);

/** A card's error line. `snapshot` is the connector state the error is about: the FIRST state
 *  a load shows after the error (whichever load applies first, the card's own reload or a poll),
 *  null until then. `refreshes` counts the connector updates seen since the error. */
export interface CardError {
  text: string;
  kind: "state" | "moment";
  snapshot: { enabled: boolean; status: string } | null;
  refreshes: number;
}

export const newCardError = (text: string, errorCode: string | null): CardError =>
  ({ text, kind: errorCode !== null && STATE_REFUSALS.has(errorCode) ? "state" : "moment", snapshot: null, refreshes: 0 });

/** The card saw a new connector state (a load or a mutation answer applied). */
export const cardErrorSawUpdate = (e: CardError, c: { enabled: boolean; status: string }): CardError =>
  ({ ...e, snapshot: e.snapshot ?? { enabled: c.enabled, status: c.status }, refreshes: e.refreshes + 1 });

/** Is a card's error still worth showing?
 *  • Until the first update after it: yes (that update is what it gets pinned to).
 *  • A state refusal ("turn it on before running it"): while that pinned state still holds.
 *  • A moment refusal ("already running", "wait before testing", "someone else just changed
 *    this connector", a network error): through the reload that follows it, then gone at the
 *    next refresh, so it never sits on the card for as long as the tab is open. */
export function errorStillApplies(e: CardError, c: { enabled: boolean; status: string }): boolean {
  if (e.snapshot === null) return true;
  if (e.kind === "moment") return e.refreshes < 2;
  return e.snapshot.enabled === c.enabled && e.snapshot.status === c.status;
}

/** Latest-wins for async loads: each load takes a token; a mutation answer applied in between
 *  invalidates every token already taken, so a load that READ before the change can't undo it.
 *  Shared by Settings → Connectors and Sync health. */
export function createLatestGate() {
  let current = 0;
  return {
    begin: () => ++current,
    invalidate: () => { current++; },
    isCurrent: (token: number) => token === current,
  };
}
export type LatestGate = ReturnType<typeof createLatestGate>;

/** "Load older" with re-asks: fetch the page older than the card's cursor NOW, try to apply it;
 *  if the card moved meanwhile (apply refused), ask again from the new cursor. Resolves true when
 *  a page applied or there is nothing older, false after `attempts` refusals. */
export async function loadOlderWithRetry<P>(deps: {
  cursorNow: () => string | null;
  fetchPage: (cursor: string) => Promise<P>;
  apply: (cursor: string, page: P) => boolean;
  attempts?: number;
}): Promise<boolean> {
  for (let i = 0; i < (deps.attempts ?? LOAD_OLDER_ATTEMPTS); i++) {
    const cursor = deps.cursorNow();
    if (!cursor) return true;
    const page = await deps.fetchPage(cursor);
    if (deps.apply(cursor, page)) return true;
  }
  return false;
}
